import { useState, useCallback } from "react";

/**
 * Hook to run an async function that streams chunks (via onChunk) and returns parsed JSON.
 * Accumulates chunks into streamingContent and sets parsedResult when the function resolves.
 *
 * @returns {{
 *   run: (asyncFn: (onChunk: (chunk: string) => void) => Promise<any>) => Promise<any>,
 *   streamingContent: string,
 *   parsedResult: any,
 *   isStreaming: boolean,
 *   error: Error | null,
 *   reset: () => void
 * }}
 */
export function useStreamingJson() {
  const [streamingContent, setStreamingContent] = useState("");
  const [parsedResult, setParsedResult] = useState(null);
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState(null);

  const run = useCallback(async (asyncFn) => {
    setStreamingContent("");
    setParsedResult(null);
    setError(null);
    setIsStreaming(true);

    const onChunk = (chunk) => {
      setStreamingContent((prev) => prev + (chunk || ""));
    };

    try {
      const result = await asyncFn(onChunk);
      setParsedResult(result);
      return result;
    } catch (err) {
      setError(err);
      throw err;
    } finally {
      setIsStreaming(false);
    }
  }, []);

  const reset = useCallback(() => {
    setStreamingContent("");
    setParsedResult(null);
    setError(null);
    setIsStreaming(false);
  }, []);

  return { run, streamingContent, parsedResult, isStreaming, error, reset };
}

export default useStreamingJson;
